import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Check, Users } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchManagerInboxRequest, fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchDomainesRequest, fetchThemesRequest } from '../../../redux/actions/paramDomainesThemesActions';
import SkillRadar from '../components/SkillRadar';
import { buildDomainThemeRadarOptions, buildRadarFilterOptions, filterRadarData, filterUsersByRadarFilters, isSkillsCampaign, RadarFiltersPanel, toRadarSkill } from '../radarFilters';
import '../EvaluationDesign.css';

export default function RadarPage() {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth || {});
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { domaines = [], themes = [] } = useSelector(state => state.paramDomainesThemes || {});
    const { inboxManager, skillEvaluations, loading } = useSelector(state => state.skillEvaluation || { inboxManager: [], skillEvaluations: [], loading: false });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [radarFilters, setRadarFilters] = useState({
        entity: '',
        poste: '',
        domaine: '',
        theme: '',
        competence: '',
    });

    const isManagerOrRH = useMemo(() => {
        const role = (user?.role?.name || user?.role || "").toUpperCase();
        return role.includes("MANAGER") || role.includes("RH") || role.includes("ADMIN");
    }, [user]);

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
        if (isManagerOrRH) {
            dispatch(fetchManagerInboxRequest());
        } else if (user?.id || user?.uid) {
            dispatch(fetchSkillEvalsByUserRequest(user.id || user.uid));
        }
    }, [dispatch, isManagerOrRH, user]);

    // Team filtering for Manager View
    const team = useMemo(() => {
        return (inboxManager || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            color: '#204385'
        }));
    }, [inboxManager]);

    const ownRadarSource = useMemo(() => {
        const evaluations = skillEvaluations || [];
        if (!selectedCampagneId) return evaluations;
        return evaluations.filter((evaluation) => Number(evaluation.campagneId) === Number(selectedCampagneId));
    }, [skillEvaluations, selectedCampagneId]);
    const radarFilterOptions = useMemo(() => {
        const radarOptions = buildRadarFilterOptions(isManagerOrRH ? team : ownRadarSource);
        return {
            ...radarOptions,
            ...buildDomainThemeRadarOptions(domaines, themes),
            competences: radarOptions.competences,
        };
    }, [isManagerOrRH, team, ownRadarSource, domaines, themes]);

    const filteredTeam = useMemo(() =>
        filterUsersByRadarFilters(team, { ...radarFilters, search: searchQuery }),
        [team, radarFilters, searchQuery]
    );

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true && isSkillsCampaign(c)), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const radarData = useMemo(() => {
        let evals = [];
        if (isManagerOrRH) {
            if (!selectedUser) return [];
            evals = [selectedUser]; // In Manager view, the "inbox" item already has the data
        } else {
            evals = skillEvaluations;
        }

        const found = evals.find(e => !selectedCampagneId || Number(e.campagneId) === Number(selectedCampagneId));
        if (!found || !found.niveaux) return [];

        return filterRadarData(found.niveaux.map(n => toRadarSkill(n)), radarFilters);
    }, [isManagerOrRH, selectedUser, skillEvaluations, selectedCampagneId, radarFilters]);

    const radarTitle = isManagerOrRH
        ? selectedUser?.nom
        : `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username || user?.email || 'Collaborateur';
    const radarSubtitle = isManagerOrRH
        ? selectedUser?.poste || selectedUser?.jobName
        : user?.jobName || user?.poste || user?.role?.name || user?.role;

    return (
        <div className="eval-page eval-radar-page eval-radar-page--simple">
            <div className="eval-header eval-radar-header">
                <div>
                    <h1>Radar de Compétences</h1>
                    <p>Visualisez l'adéquation entre les scores obtenus et les exigences du poste.</p>
                </div>
            </div>


            <RadarFiltersPanel
                filters={radarFilters}
                setFilters={setRadarFilters}
                options={radarFilterOptions}
                showUserFilters={false}
                campaignValue={selectedCampagneId}
                onCampaignChange={setSelectedCampagneId}
                campaignOptions={activeCampagnes}
                campaignPlaceholder="Selectionnez une campagne"
            />

            <div className="eval-layout">
                {isManagerOrRH && (
                    <aside className="eval-sidebar">
                        <div className="eval-toolbar">
                            <div className="eval-search-box">
                                <Search className="eval-search-icon" size={16} />
                                <input 
                                    type="text" 
                                    className="eval-input" 
                                    placeholder="Chercher collaborateur..." 
                                    value={searchQuery} 
                                    onChange={(e) => setSearchQuery(e.target.value)} 
                                />
                            </div>
                        </div>
                        <div className="eval-list">
                            {filteredTeam.map(u => (
                                <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                    <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{u.nom}</div>
                                        <div className="eval-user-sub">{u.campagneNom}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>
                )}

                <main className="eval-main">
                    {isManagerOrRH && !selectedUser ? (
                        <div className="eval-empty">
                            <Users size={64} className="eval-empty-icon" />
                            <h3>Analyse d'Équipe</h3>
                            <p>Sélectionnez un membre de votre équipe à gauche pour voir son radar.</p>
                        </div>
                    ) : radarData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                {/*<div className="eval-section-title" style={{ justifyContent: 'center' }}>*/}
                                {/*    Radar - {isManagerOrRH ? selectedUser.nom : user.firstName + ' ' + user.lastName}*/}
                                {/*</div>*/}
                                <SkillRadar data={radarData} title={radarTitle} subtitle={radarSubtitle} />
                                {/*<div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>*/}
                                {/*    Le polygone <strong>bleu</strong> représente les compétences validées.<br/>*/}
                                {/*    La bordure <strong>corail</strong> représente le niveau attendu sur le poste.*/}
                                {/*</div>*/}
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation validée n'a été trouvée pour cette campagne.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
